import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { isEmpty } from 'lodash';

import CreateClubFormComponent from '../../components/CreateClubForm';
import CreateTeamTennisClubFormComponent from '../../components/EditTeamTennisClubForm';
import requireAuth from '../../components/hocs/requireAuth';
import requireOnboarding from '../../components/hocs/requireOnboarding';
import * as action from '../../actions/club-actions';

import { uploadTempPhoto, clearTempPhoto } from '../../actions/upload-actions';
import PhotoEditor from '../../components/PhotoUpload/PhotoEditor';
import { isClubAdminOrOwner, isPersonalClub, isTeamTennisClub } from '../../utils/clubs';
import StatsigRedirect from '../../components/redirect/StatsigRedirect';
import { featureGates } from '../../utils/statsig';
import { useStatsig } from '../../components/hooks/useStatsig';

import config from '../../../config';

import styles from './clubEdit.scss';

const clubTypes = {
    CLUBS: 'clubs',
    SCHOOLS: 'schools',
    COLLEGES: 'colleges',
    GROUPS: 'groups',
};
const newClubPaths = {
    clubs: 'club',
    schools: 'high-school',
    colleges: 'college',
};
const _ = { isEmpty };

const ClubEditPage = (props) => {
    const [submitting, setSubmitting] = useState(false);
    const [showPhotoEditor, setShowPhotoEditor] = useState(false);
    const photoEditorType = 'club_sponsor_logo';

    const { auth, club, uploads } = useSelector((state) => state);
    const dispatch = useDispatch();
    const { checkGate } = useStatsig();
    const isClubRedirectEnabled = checkGate(featureGates.CLUB_LINKS_REDIRECT);
    const nextAppHost = config.nextAppHost || process.env.NEXT_APP_HOST;

    const { clubDetails, clubId } = club;
    const { clubId: clubIdInUrl, clubType } = props.match.params;
    const clubTierType = clubDetails?.tierType || {};
    const { user } = auth;
    const isAdultTeamTennisClub = isTeamTennisClub(clubTierType);

    useEffect(() => {
        dispatch(action.setClubId(clubIdInUrl));
    }, [clubIdInUrl]);

    useEffect(() => {
        if (club.isClubUpdated && club.clubDetails) {
            const { clubType: typeFromParams } = props.match.params;
            const { clubDetails: updatedClubDetails } = club;
            props.history.push(`/${typeFromParams}/${updatedClubDetails.id}`);
        }
    }, [club.isClubUpdated, club.clubDetails, props.history, props.match.params, club]);

    const handleSubmit = (values) => {
        setSubmitting(true);
        values.values.clubName = values.values.clubName.trim();
        if (values.values.about) {
            values.values.about = values.values.about.trim();
        }
        if (values.values.website) {
            values.values.website = values.values.website.trim();
        }
        dispatch(action.updateClub(values.values));
        setSubmitting(false);
    };

    const handleHidePhotoEditor = () => {
        setShowPhotoEditor(false);
        dispatch(clearTempPhoto(true));
    };

    const handleUploadLogoStart = () => {
        setShowPhotoEditor(true);
    };

    const handleUploadPhoto = (file) => {
        dispatch(uploadTempPhoto(file));
    };

    const handleCropSponsorLogo = async (values) => {
        const { clubId: currentClubId } = club;
        await dispatch(action.cropClubSponsorLogo(currentClubId, values));
        dispatch(clearTempPhoto(true));
        setShowPhotoEditor(false);
    };
    /* End photo upload */

    // rewrite route if type mistmatch
    if (!_.isEmpty(clubTierType) && clubId === clubIdInUrl) {
        if (clubType === clubTypes.CLUBS) {
            if (clubTierType.isCollege) {
                return <Redirect to={`/colleges/${clubId}/edit`} />;
            }
            if (clubTierType.isHighSchool) {
                return <Redirect to={`/schools/${clubId}/edit`} />;
            }
            if (isPersonalClub(clubTierType)) {
                return <Redirect to={`/groups/${clubId}/edit`} />;
            }
        } else if (clubType === clubTypes.SCHOOLS) {
            if (clubTierType.isCollege) {
                return <Redirect to={`/colleges/${clubId}/edit`} />;
            }
            if (isPersonalClub(clubTierType)) {
                return <Redirect to={`/groups/${clubId}/edit`} />;
            }
            if (!clubTierType.isHighSchool) {
                return <Redirect to={`/clubs/${clubId}/edit`} />;
            }
        } else if (clubType === clubTypes.COLLEGES) {
            if (clubTierType.isHighSchool) {
                return <Redirect to={`/schools/${clubId}/edit`} />;
            }
            if (isPersonalClub(clubTierType)) {
                return <Redirect to={`/groups/${clubId}/edit`} />;
            }
            if (!clubTierType.isCollege) {
                return <Redirect to={`/clubs/${clubId}/edit`} />;
            }
        } else if (clubType === clubTypes.GROUPS) {
            if (clubTierType.isCollege) {
                return <Redirect to={`/colleges/${clubId}/edit`} />;
            }
            if (clubTierType.isHighSchool) {
                return <Redirect to={`/schools/${clubId}/edit`} />;
            }
            if (!isPersonalClub(clubTierType)) {
                return <Redirect to={`/clubs/${clubId}/edit`} />;
            }
        }
    }

    const profileRedirectPath = isClubRedirectEnabled ? `${nextAppHost}/${newClubPaths[clubType]}/${clubId}` : `/${clubType}/${clubId}`;

    return (
        <div className={`${styles.section}`}>

            <div className={`container h2 bg-transparent`}>
                {clubDetails && (
                    <div className={`text-center ${styles.clubName}`} data-testid="club-edit.page.club-name">
                        {clubDetails.name}
                    </div>
                )}
                <h2 className="text-center" data-testid="club-edit.page.title">Edit Profile</h2>
            </div>

            {clubDetails && isAdultTeamTennisClub && (
                clubDetails.currentMember && isClubAdminOrOwner(clubDetails) && isAdultTeamTennisClub
                    ? (
                        <CreateTeamTennisClubFormComponent
                            profileRedirectPath={profileRedirectPath}
                            handleSubmit={handleSubmit}
                            submitting={submitting}
                            handleUploadPhoto={handleUploadPhoto}
                            handleUploadLogoStart={handleUploadLogoStart}
                            defaultValues={{
                                ...clubDetails,
                                id: clubId,
                                about: clubDetails.description,
                                clubName: clubDetails.name,
                                website: clubDetails.url,
                                verificationApplicationStatus: clubDetails.verificationApplicationStatus,
                                sponsorLogoUrl: clubDetails.sponsorLogoUrl,
                            }}
                            user={user}
                        />
                    )
                    : (
                        <StatsigRedirect
                            gate={featureGates.CLUB_LINKS_REDIRECT}
                            enabledTo={`${config.nextAppHost}/${newClubPaths[clubType]}/${clubDetails.id}`} // Next.js route
                            disabledTo={`/${clubType}/${clubDetails.id}`} // legacy route
                        />
                    )
            )}

            {clubDetails && !isAdultTeamTennisClub && (
                clubDetails.currentMember && (clubDetails.currentMember.role === 'Admin' || clubDetails.currentMember.role === 'Owner')
                    ? (
                        <CreateClubFormComponent
                            profileRedirectPath={profileRedirectPath}
                            handleSubmit={handleSubmit}
                            submitting={submitting}
                            handleUploadPhoto={handleUploadPhoto}
                            handleUploadLogoStart={handleUploadLogoStart}
                            defaultValues={{
                                ...clubDetails,
                                id: clubId,
                                about: clubDetails.description,
                                clubName: clubDetails.name,
                                website: clubDetails.url,
                                verificationApplicationStatus: clubDetails.verificationApplicationStatus,
                                sponsorLogoUrl: clubDetails.sponsorLogoUrl,
                            }}
                            user={user}
                        />
                    )
                    : (
                        <StatsigRedirect
                            gate={featureGates.CLUB_LINKS_REDIRECT}
                            enabledTo={`${config.nextAppHost}/${newClubPaths[clubType]}/${clubDetails.id}`} // Next.js route
                            disabledTo={`/${clubType}/${clubDetails.id}`} // legacy route
                        />
                    )
            )}
            <PhotoEditor
                showEditor={showPhotoEditor}
                closeEditor={handleHidePhotoEditor}
                imageUrl={uploads.tempPhotoUrl}
                uploadCropData={handleCropSponsorLogo}
                type={photoEditorType}
                data-testid="modal.club-edit.photo-editor"
            />
        </div>
    );
};

export default requireAuth(requireOnboarding(ClubEditPage));
